/**
 * Defines the structure for environment-specific configurations.
 */
interface EnvironmentConfig {
  isTest: boolean; // Indicates if the environment is for testing purposes.
  isDevelopment: boolean; // Indicates if the environment is for development purposes.
}

/**
 * Provides a service to manage and access configuration settings based on the current environment.
 * This service simplifies the process of using environment-specific configurations throughout your application,
 * ensuring that the right settings are used according to the deployment environment.
 */
export class ConfigService {
  /**
   * Holds the configurations for different environments.
   */
  private config: Record<string, EnvironmentConfig> = {
      development: { isTest: false, isDevelopment: true },
      test: { isTest: true, isDevelopment: false },
      production: { isTest: false, isDevelopment: false }
  };

  /**
   * Initializes the ConfigService by validating the current environment.
   * Throws an error if the environment is not recognized, ensuring that all configurations
   * are valid and intentional.
   */
  constructor() {
      const env = this.getEnvironment();
      if (!this.config[env]) {
          throw new Error(`Unknown environment: ${env}`);
      }
  }

  /**
   * Retrieves the current environment, defaulting to 'development' if not set.
   * This method is primarily used internally but can also be useful for logging and debugging.
   *
   * @returns {string} The current environment identifier.
   */
  getEnvironment(): string {
      return process.env.NODE_ENV || 'development';
  }

  /**
   * Determines if the current environment is specifically for testing.
   * This can be used to enable or disable application features and behaviors
   * that are only appropriate for testing scenarios.
   *
   * @returns {boolean} True if the current environment is for testing, otherwise false.
   */
  isTestEnvironment(): boolean {
      return this.config[this.getEnvironment()].isTest;
  }

  /**
   * Determines if the current environment is specifically for development.
   * This is often used to enable debugging tools and verbose logging that should not be
   * present in production environments.
   *
   * @returns {boolean} True if the current environment is for development, otherwise false.
   */
  isDevelopmentEnvironment(): boolean {
      return this.config[this.getEnvironment()].isDevelopment;
  }

  /**
   * Determines if the current environment is specifically for production.
   * This is often used to enable logging monitoring that should not be
   * present in debugging environments.
   * 
   * @returns {boolean} True if the current environment is for production, otherwise false.
   */
  isProductionEnvironment(): boolean {
    if (!this.config[this.getEnvironment()].isTest && !this.config[this.getEnvironment()].isDevelopment) {
        return true;
    }
    return false;
  }
}